import { FC, useLayoutEffect, useState, useRef } from 'react';
import classnames from 'classnames';

import { DotPagesProps } from '@hh.ru/magritte-ui-dot-pages/types';
import { ProgressBar } from '@hh.ru/magritte-ui-progress-bar';

import styles from './dot-pages.less';

export const DotPages: FC<DotPagesProps> = ({
    active,
    progressProps,
    visibleCount = 6,
    count,
    activeDynamic,
    'data-qa': dataQa = 'dot-pages-container',
}) => {
    const [width, setWidth] = useState<number | string>(0);
    const [offset, setOffset] = useState<number>(0);
    const [isLeftFade, setLeftFade] = useState(false);
    const [isRightFade, setRightFade] = useState(false);
    const activeIndex = active - 1;
    const isOddVisibleCount = visibleCount % 2 !== 0;

    const sizes = useRef<{
        dotWidth: number;
        dotDynamicWidth: number;
    }>({
        dotWidth: 0,
        dotDynamicWidth: 0,
    });

    useLayoutEffect(() => {
        let { dotWidth, dotDynamicWidth } = sizes.current;
        if (!dotWidth || !dotDynamicWidth) {
            const dotElement = document.createElement('div');
            const dotDynamicElement = document.createElement('div');
            dotElement.classList.add(styles.item);
            dotDynamicElement.classList.add(styles.item, styles.dynamic);
            document.body.appendChild(dotElement);
            document.body.appendChild(dotDynamicElement);
            const dotStyle = window.getComputedStyle(dotElement);

            dotWidth = dotElement.offsetWidth + parseInt(dotStyle.marginLeft, 10) + parseInt(dotStyle.marginRight, 10);
            const dotDynamicStyle = window.getComputedStyle(dotDynamicElement);
            dotDynamicWidth =
                dotDynamicElement.offsetWidth +
                parseInt(dotDynamicStyle.marginLeft, 10) +
                parseInt(dotDynamicStyle.marginRight, 10);
            sizes.current.dotDynamicWidth = dotDynamicWidth;
            sizes.current.dotWidth = dotWidth;
            document.body.removeChild(dotElement);
            document.body.removeChild(dotDynamicElement);
        }

        const withScroll = visibleCount < count;

        if (!withScroll) {
            setWidth('auto');
            setOffset(0);
            setLeftFade(false);
            setRightFade(false);
            return;
        }
        const containerWidth = visibleCount * dotWidth + (activeDynamic ? dotDynamicWidth - dotWidth : 0) + dotWidth;
        setWidth(containerWidth);
        const halfContainerWidth = containerWidth / 2;

        // Ширина элементов справа активного
        const right = (count - activeIndex - 1) * dotWidth;
        const activeRightWidth = right + (activeDynamic ? dotDynamicWidth / 2 : dotWidth / 2);
        // Ecли половина активного элемента и элементы справа него влезли
        // в половину ширины контейнера - нужно остановить скрол
        const stopScroll = activeRightWidth <= halfContainerWidth;

        // Ширина элементов слева активного
        const left = activeIndex * dotWidth;
        // Сколько места слева занимает текущий элемент = ширина точек до него + половина его ширины
        // Прибавляю половину ширины, потому что если не влезло больше половины - нужно сроллить
        const activeWidth = left + (activeDynamic ? dotDynamicWidth / 2 : dotWidth / 2);
        // Ecли половина активного элемента и элементы слева него не влезли
        // в половину контейнера нужно скролить
        const startScroll = activeWidth > halfContainerWidth;

        // Если для текущего элемента не хватает места слева (он не помещается до середины контейнера)
        // и он не в конце контейнера,
        // то скроллим к нему, чтобы он оказался по центру контейнера
        if (startScroll && !stopScroll) {
            setOffset(halfContainerWidth - activeWidth);
            setLeftFade(true);
            setRightFade(true);
            // Если элемент на индексе, после которого не нужно скролить,
            // то остаемся на координате, чтобы скролящийся контейнер заканчивался там же, где и основной
        } else if (stopScroll) {
            const commonWidth = count * dotWidth + (activeDynamic ? dotDynamicWidth - dotWidth : 0);
            setOffset(containerWidth - commonWidth);
            setLeftFade(true);
            setRightFade(false);
        } else {
            setOffset(0);
            setLeftFade(false);
            setRightFade(true);
        }
    }, [activeIndex, visibleCount, activeDynamic, count]);

    return (
        <div
            className={classnames(styles.wrapContainer, {
                [styles.fadeLeftSide]: isLeftFade && !isRightFade,
                [styles.fadeRightSide]: isRightFade && !isLeftFade,
                [styles.fadeBothSides]: isRightFade && isLeftFade && !isOddVisibleCount,
                [styles.fadeBothSidesOdd]: isRightFade && isLeftFade && isOddVisibleCount,
            })}
            style={{ width }}
            data-qa={dataQa}
        >
            <div
                className={styles.container}
                style={{ transform: `translate3d(${offset}px, 0, 0)` }}
                data-qa="dot-pages-scrollable-container"
            >
                {[...Array(count).keys()].map((_, i) => {
                    const isDynamic = i === activeIndex && activeDynamic;
                    return (
                        <div
                            key={i}
                            className={classnames(styles.item, {
                                [styles.active]: i === activeIndex,
                                [styles.dynamic]: isDynamic,
                            })}
                            data-qa="dot-pages-item"
                        >
                            {isDynamic && progressProps && <ProgressBar {...progressProps} />}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
